import { useEffect, useRef } from "react";
import type { cur8PageViewEvents } from "@ifgengineering/client-analytics/src/types";
import useTrackEvent from "./useTrackEvent";

/**
 * Fires a Mixpanel page view once the page is ready, mirroring the mobile hook
 * of the same name. The generic `pageView` handler attaches `app` and
 * `channel`; a failed call never throws into the page.
 *
 * `ready` holds the event until the properties it carries are real, so a page
 * whose deal arrives a render later reports a name rather than a blank. `key`
 * scopes "once" - passing the id of the thing being viewed refires when the
 * page is reused for a different one, which client-side routing does.
 */
export interface UseScreenViewOptions {
  ready?: boolean;
  key?: string;
  extraProps?: Record<string, string | number | boolean | undefined>;
}

export const useScreenView = (
  eventName: cur8PageViewEvents | (string & object),
  { ready = true, key, extraProps }: UseScreenViewOptions = {}
): void => {
  const trackPageView = useTrackEvent("pageView");
  const firedKeyRef = useRef<string | null>(null);

  // Read through a ref so an object literal at the call site cannot refire.
  const extraPropsRef = useRef(extraProps);
  extraPropsRef.current = extraProps;

  useEffect(() => {
    if (!ready) return;
    const firedKey = key ?? eventName;
    if (firedKeyRef.current === firedKey) return;
    firedKeyRef.current = firedKey;
    trackPageView({ eventName, ...extraPropsRef.current });
  }, [eventName, key, ready, trackPageView]);
};

export default useScreenView;
